import { Command } from 'cmdk'
import { ChevronLeft, ChevronRight, Plus, Search } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { isRecent } from '@/components/ui/recency'
import { relativeDayLabel } from '@/lib/relative-date'
import { useCreatePickerCategory } from '@/lib/time-queries'
import type { PickerData, ProjectOption } from '@/lib/types'
import { useIsCompact } from '@/lib/use-media-query'
import { cn } from '@/lib/utils'
import {
  categoriesForProject,
  chooseClient,
  chooseProject,
  clientOptions,
  matchesTokens,
  projectsForClient,
  selectionFromProject,
  type MenuChoice,
  type MenuStep,
  type NewRowSelection,
} from './week-rows'

interface EntryPickerProps {
  pickers: PickerData
  /** Waar de kiezer opent; standaard bij de klanten. */
  start?: MenuStep
  /** De huidige bestemming, die als zodanig gemarkeerd wordt. */
  current?: { clientId: string; projectId: string; categoryId: string | null }
  /** Er loopt een wissel; kiezen kan pas weer als die klaar is. */
  pending?: boolean
  onPick: (selection: NewRowSelection) => void
}

const itemClass =
  'flex cursor-pointer items-center justify-between gap-3 rounded-md px-2.5 py-2 text-sm text-ink aria-selected:bg-accent-600/10 aria-selected:text-accent-700 data-[disabled=true]:cursor-wait data-[disabled=true]:opacity-50'

const hintClass = 'flex shrink-0 items-center gap-1 text-xs text-ink-faint'

/**
 * Het venster waarbinnen "wanneer voor het laatst" nog iets zegt. De ureninvoer heeft een
 * weekritme; daarbuiten is "veertig weken terug" ruis die elke regel van een label voorziet
 * zonder de volgorde te verklaren.
 */
const RECENT_WINDOW_DAYS = 60

function recencyHint(lastBookedAt: string | null): string | null {
  return lastBookedAt !== null && isRecent(lastBookedAt, RECENT_WINDOW_DAYS) ? relativeDayLabel(lastBookedAt) : null
}

/**
 * De ene kiezer voor een bestemming: klant, dan project, dan categorie. Hij kiest alleen
 * en schrijft niets weg, zodat dezelfde lijst zowel een nieuwe regel als het verplaatsen
 * van een bestaande kan bedienen.
 *
 * Elke lijst staat op recentheid: waar je de laatste weken op boekte staat bovenaan, de
 * rest alfabetisch eronder. Daarmee is een aparte "vaak geboekt"-lijst overbodig, en houd
 * je één plek om te kijken in plaats van twee.
 *
 * Het veld bovenin filtert altijd de stap waar je staat, en nooit meer dan dat. Een veld
 * dat soms de hele catalogus doorzoekt en soms niet is niet te voorspellen; dit wel.
 */
export function EntryPicker({ pickers, start, current, pending, onPick }: EntryPickerProps) {
  const [stack, setStack] = useState<MenuStep[]>(() => [start ?? { kind: 'client' }])
  const [filter, setFilter] = useState('')
  const [creating, setCreating] = useState<ProjectOption | null>(null)
  const [newCategory, setNewCategory] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const createCategory = useCreatePickerCategory()
  const compact = useIsCompact()

  const step = stack[stack.length - 1]

  function go(next: MenuStep) {
    setStack((prev) => [...prev, next])
    setFilter('')
    inputRef.current?.focus()
  }

  function back() {
    if (stack.length === 1) return
    setStack((prev) => prev.slice(0, -1))
    setFilter('')
    inputRef.current?.focus()
  }

  function apply(choice: MenuChoice) {
    if ('step' in choice) {
      go(choice.step)
      return
    }
    onPick(selectionFromProject(choice.commit.project, choice.commit.category))
  }

  function submitNewCategory() {
    const name = newCategory.trim()
    if (name === '' || !creating) return
    createCategory.mutate(
      { clientId: creating.clientId, projectId: creating.id, name },
      {
        onSuccess: (created) =>
          onPick({ ...selectionFromProject(creating, null), categoryId: created.id, categoryName: created.name }),
      },
    )
  }

  const clients = useMemo(
    () => (step.kind === 'client' ? clientOptions(pickers).filter((c) => matchesTokens(c.name, filter)) : []),
    [step, pickers, filter],
  )

  const projects = useMemo(
    () =>
      step.kind === 'project'
        ? projectsForClient(pickers, step.clientId).filter((p) => matchesTokens(`${p.name} ${p.code ?? ''}`, filter))
        : [],
    [step, pickers, filter],
  )

  const categories = useMemo(
    () => (step.kind === 'category' ? categoriesForProject(pickers, step.project.id).filter((c) => matchesTokens(c.name, filter)) : []),
    [step, pickers, filter],
  )

  const placeholder =
    step.kind === 'client' ? 'Filter klanten…' : step.kind === 'project' ? 'Filter projecten…' : 'Filter categorieen…'

  const crumb =
    step.kind === 'client' ? null : step.kind === 'project' ? step.clientName : `${step.project.clientName} · ${step.project.name}`

  if (creating) {
    return (
      <div className="p-3">
        <p className="label-caps mb-1.5">Nieuwe categorie voor {creating.name}</p>
        <input
          autoFocus
          value={newCategory}
          onChange={(event) => setNewCategory(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return
            event.preventDefault()
            submitNewCategory()
          }}
          placeholder="Naam categorie"
          aria-label="Naam categorie"
          className="field mb-2"
        />
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost h-8 px-2.5 text-sm" onClick={() => setCreating(null)}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn-primary h-8 px-2.5 text-sm"
            onClick={submitNewCategory}
            disabled={newCategory.trim() === '' || createCategory.isPending}
          >
            {createCategory.isPending ? 'Bezig…' : 'Toevoegen'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <Command shouldFilter={false} label="Bestemming kiezen" className="flex min-h-0 flex-1 flex-col">
      {crumb ? (
        <button
          type="button"
          onClick={back}
          disabled={stack.length === 1}
          className="flex items-center gap-1 border-b border-hairline px-2.5 py-1.5 text-left text-xs font-medium text-ink-faint hover:text-ink disabled:hover:text-ink-faint"
        >
          <ChevronLeft className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="truncate">{crumb}</span>
        </button>
      ) : null}

      <div className="relative border-b border-hairline">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" aria-hidden="true" />
        <Command.Input
          ref={inputRef}
          // Op telefoonformaat schuift het toetsenbord anders meteen over de lijst heen,
          // en juist daar is de cascade de bedoelde weg.
          autoFocus={!compact}
          value={filter}
          onValueChange={setFilter}
          onKeyDown={(event) => {
            // Backspace in een leeg veld leest als "een stap terug", zoals in een kruimelpad.
            if (event.key !== 'Backspace' || filter !== '' || stack.length === 1) return
            event.preventDefault()
            back()
          }}
          placeholder={placeholder}
          className="h-11 w-full bg-transparent pl-9 pr-3 text-sm text-ink placeholder:text-ink-faint focus:outline-hidden"
        />
      </div>

      <Command.List className="min-h-0 flex-1 overflow-y-auto p-1.5 max-sm:max-h-[60vh] sm:max-h-72">
        {clients.map((client) => (
          <Command.Item
            key={client.id}
            value={`cli:${client.id}`}
            disabled={pending}
            onSelect={() => apply(chooseClient(client, pickers))}
            className={cn(itemClass, client.id === current?.clientId && 'text-accent-700')}
          >
            <span className="truncate">{client.name}</span>
            <span className={hintClass}>
              {recencyHint(client.lastBookedAt)}
              <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
          </Command.Item>
        ))}

        {projects.map((project) => {
          const hasNext = categoriesForProject(pickers, project.id).length > 0 || project.categoryRequired
          return (
            <Command.Item
              key={project.id}
              value={`prj:${project.id}`}
              disabled={pending}
              onSelect={() => apply(chooseProject(project, pickers))}
              className={cn(itemClass, project.id === current?.projectId && 'text-accent-700')}
            >
              <span className="truncate">{project.name}</span>
              <span className={hintClass}>
                {recencyHint(project.lastBookedAt)}
                {hasNext ? <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" /> : null}
              </span>
            </Command.Item>
          )
        })}

        {step.kind === 'category' ? (
          <>
            {!step.project.categoryRequired && filter.trim() === '' ? (
              <Command.Item
                value="cat:none"
                disabled={pending}
                onSelect={() => onPick(selectionFromProject(step.project, null))}
                className={cn(itemClass, 'text-ink-soft')}
              >
                Geen categorie
              </Command.Item>
            ) : null}
            {categories.map((category) => (
              <Command.Item
                key={category.id}
                value={`cat:${category.id}`}
                disabled={pending}
                onSelect={() => onPick(selectionFromProject(step.project, category))}
                className={cn(
                  itemClass,
                  step.project.id === current?.projectId && category.id === current?.categoryId && 'text-accent-700',
                )}
              >
                <span className="truncate">{category.name}</span>
                <span className={hintClass}>{recencyHint(category.lastBookedAt)}</span>
              </Command.Item>
            ))}
            <Command.Item
              value="cat:new"
              disabled={pending}
              onSelect={() => {
                setNewCategory(filter.trim())
                setCreating(step.project)
              }}
              className={cn(itemClass, 'mt-0.5 border-t border-hairline font-medium text-accent-700')}
            >
              <span className="flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                Nieuwe categorie
              </span>
            </Command.Item>
          </>
        ) : null}

        {step.kind !== 'category' && clients.length === 0 && projects.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-ink-faint">Niets gevonden.</p>
        ) : null}
      </Command.List>
    </Command>
  )
}
